// azureVisionService.js
// Import not needed in browser context, we'll use mock functionality
// import { ComputerVisionClient } from "@azure/cognitiveservices-computervision";
// import { DocumentAnalysisClient } from "@azure/ai-form-recognizer";
// import { AzureKeyCredential } from "@azure/core-auth";
import { BACKEND_CONFIG, FEATURES, MOCK_DATA } from '../config.js';

// Azure AI configuration for client-side simulation
const API_URL = BACKEND_CONFIG.API_URL;

/**
 * Process a PDF file using Azure AI services to extract grocery items
 * Note: In browser context, we use simulated Azure services
 * @param {ArrayBuffer} pdfBuffer - The PDF file as an ArrayBuffer
 * @param {Array} pdfPages - Array of page images from the PDF
 * @returns {Promise<Array>} - Array of extracted grocery items
 */
export async function processGroceryLeaflet(pdfBuffer, pdfPages = []) {
    try {
        // In a real backend environment, we would use Azure services
        // In the browser, we'll simulate the process for demonstration
        
        if (!FEATURES.AZURE_VISION_ENABLED || MOCK_DATA.USE_MOCK_DATA) {
            console.log("Using mock extraction instead of Azure services");
            return simulateAzureExtraction(pdfPages);
        }
        
        // If no pages were provided, return an empty array
        if (pdfPages.length === 0) {
            console.error("No PDF pages provided for analysis");
            return [];
        }
        
        // In a production environment with a backend:
        // 1. We would send these images to a backend service
        // 2. The backend would use Azure SDKs to process them
        // 3. The results would be returned to the frontend
        
        // For this demo, we'll simulate Azure processing
        console.log(`Processing ${pdfPages.length} pages with simulated Azure services`);
        
        // Simulate processing delay
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Extract text from images using simple canvas-based OCR simulation
        const extractedItems = await simulateAzureExtraction(pdfPages);
        
        return extractedItems;
    } catch (error) {
        console.error("Error processing grocery leaflet:", error);
        return [];
    }
}

/**
 * Simulate Azure AI extraction for browser environment
 * @param {Array} pageImages - Array of page images
 * @returns {Promise<Array>} - Extracted items
 */
async function simulateAzureExtraction(pageImages) {
    // In a real implementation, we would send these images to Azure
    // For demo purposes, we'll generate some sample items based on the number of pages
    const items = [];
    let currentId = 1;
    
    const sampleItems = [
        { 
            name: 'Laxfilé hel sida', 
            category: 'seafood', 
            originalPrice: 199.00, 
            discountPrice: 149.00, 
            unit: 'kg', 
            discount: 25,
            description: 'Azure AI extraktion (simulerad)' 
        },
        { 
            name: 'Kycklingbröstfilé', 
            category: 'meat', 
            originalPrice: 91.90, 
            discountPrice: 69.90, 
            unit: 'kg', 
            discount: 23,
            description: 'Azure AI extraktion (simulerad)' 
        },
        { 
            name: 'Kvisttomater', 
            category: 'vegetables', 
            originalPrice: 39.90, 
            discountPrice: 24.90, 
            unit: 'kg', 
            discount: 37,
            description: 'Azure AI extraktion (simulerad)' 
        },
        { 
            name: 'OLW snacks', 
            category: 'snacks', 
            originalPrice: 83.70, 
            discountPrice: 59.00, 
            unit: '3-pack', 
            discount: 30,
            description: 'Azure AI extraktion (simulerad)' 
        },
        { 
            name: 'Gul lök', 
            category: 'vegetables', 
            originalPrice: 12.90, 
            discountPrice: 9.90, 
            unit: 'kg', 
            discount: 23,
            description: 'Azure AI extraktion (simulerad)' 
        },
        { 
            name: 'Röda äpplen', 
            category: 'fruit', 
            originalPrice: 45.00, 
            discountPrice: 39.90, 
            unit: '2.5kg', 
            discount: 11,
            description: 'Azure AI extraktion (simulerad)' 
        },
        { 
            name: 'Fläskfilé', 
            category: 'meat', 
            originalPrice: 129.00, 
            discountPrice: 99.90, 
            unit: 'kg', 
            discount: 23,
            description: 'Azure AI extraktion (simulerad)' 
        },
        { 
            name: 'Färsk pasta', 
            category: 'pantry', 
            originalPrice: 29.90, 
            discountPrice: 19.90, 
            unit: '250g', 
            discount: 33,
            description: 'Azure AI extraktion (simulerad)' 
        },
        { 
            name: 'Färskost', 
            category: 'dairy', 
            originalPrice: 32.90, 
            discountPrice: 25.90, 
            unit: '200g', 
            discount: 21,
            description: 'Azure AI extraktion (simulerad)' 
        },
        { 
            name: 'Rullrån', 
            category: 'snacks', 
            originalPrice: 19.90, 
            discountPrice: 15.90, 
            unit: 'paket', 
            discount: 20,
            description: 'Azure AI extraktion (simulerad)' 
        },
    ];
    
    // Generate different items based on the number of pages
    const itemsPerPage = 2; // Generate 2 items per page
    const totalItems = Math.min(pageImages.length * itemsPerPage, sampleItems.length);
    
    for (let i = 0; i < totalItems; i++) {
        const sampleItem = sampleItems[i % sampleItems.length]; // Loop through sample items
        
        // Create a new item with a unique ID
        items.push({
            id: currentId++,
            name: sampleItem.name,
            category: sampleItem.category,
            originalPrice: sampleItem.originalPrice,
            discountPrice: sampleItem.discountPrice,
            unit: sampleItem.unit,
            image: 'images/placeholder.jpg', // Use placeholder images
            discount: sampleItem.discount,
            description: sampleItem.description
        });
    }
    
    return items;
}

/**
 * Helper function to determine if a product name is likely a food product
 */
function isLikelyFoodProduct(name) {
    const nonFoodKeywords = ['gryta', 'tvål', 'diskmedel', 'handduk', 'kudde', 'möbel', 'duk'];
    const foodKeywords = [
        'äpple', 'banan', 'kött', 'korv', 'mjölk', 'ost', 'pasta', 
        'potatis', 'lax', 'kyckling', 'fläsk', 'nöt', 'tomat'
    ];
    
    // Check if it contains non-food keywords
    if (nonFoodKeywords.some(keyword => name.toLowerCase().includes(keyword))) {
        return false;
    }
    
    // Higher confidence if it contains food keywords
    if (foodKeywords.some(keyword => name.toLowerCase().includes(keyword))) {
        return true;
    }
    
    // Otherwise, make a best guess
    return !name.includes('kr') && name.length > 3 && name.length < 40;
}

/**
 * Helper function to determine category based on product name
 */
function determineCategory(name) {
    const name_lower = name.toLowerCase();
    
    if (name_lower.includes('lax') || name_lower.includes('torsk') || name_lower.includes('räk')) {
        return 'seafood';
    } else if (name_lower.includes('kyckling') || name_lower.includes('nöt') || name_lower.includes('fläsk') || name_lower.includes('karré')) {
        return 'meat';
    } else if (name_lower.includes('tomat') || name_lower.includes('lök') || name_lower.includes('paprika') || name_lower.includes('potatis')) {
        return 'vegetables';
    } else if (name_lower.includes('äpple') || name_lower.includes('banan') || name_lower.includes('päron') || name_lower.includes('frukt')) {
        return 'fruit';
    } else if (name_lower.includes('ost') || name_lower.includes('mjölk') || name_lower.includes('yoghurt')) {
        return 'dairy';
    } else if (name_lower.includes('snacks') || name_lower.includes('chips') || name_lower.includes('godis')) {
        return 'snacks';
    } else if (name_lower.includes('pasta') || name_lower.includes('ris') || name_lower.includes('mjöl')) {
        return 'pantry';
    } else {
        return 'other';
    }
}

/**
 * Helper function to determine unit based on product name and context
 */
function determineUnit(name, context) {
    const name_lower = name.toLowerCase();
    const context_lower = context.toLowerCase();
    
    if (context_lower.includes('kg')) {
        return 'kg';
    } else if (context_lower.includes('g ') || context_lower.includes('gram')) {
        return 'g';
    } else if (context_lower.includes('l ') || context_lower.includes('liter')) {
        return 'l';
    } else if (context_lower.includes('st ') || context_lower.includes('styck')) {
        return 'st';
    } else if (context_lower.includes('pack')) {
        return 'pack';
    } else {
        // Fallback to finding the unit in the name
        if (name_lower.includes('kg')) {
            return 'kg';
        } else if (name_lower.includes('g ')) {
            return 'g';
        } else if (name_lower.includes('l ') || name_lower.includes('liter')) {
            return 'l';
        } else if (name_lower.includes('st ') || name_lower.includes('styck')) {
            return 'st';
        } else {
            return 'st'; // Default unit
        }
    }
}

/**
 * Remove duplicate items based on name similarity
 */
function removeDuplicates(items) {
    const uniqueItems = [];
    const seenNames = new Set();
    
    for (const item of items) {
        // Normalize the name for comparison
        const normalizedName = item.name.toLowerCase().trim();
        
        // Check if we've seen a similar name
        let isDuplicate = false;
        for (const seenName of seenNames) {
            if (normalizedName.includes(seenName) || seenName.includes(normalizedName)) {
                isDuplicate = true;
                break;
            }
        }
        
        if (!isDuplicate) {
            seenNames.add(normalizedName);
            uniqueItems.push(item);
        }
    }
    
    return uniqueItems;
}
